import { createApi } from '@reduxjs/toolkit/query/react'
import { ASSIGN_CONSENT_CHECKLISTS, ASSIGN_CONSENT_FORM, CREATE_CONSENT_CHECKLISTS, DELETE_ASSIGNED_CONSENT_FORM, DELETE_CONSENT_CHECKLISTS, DELETE_GUARDIAN_DOCUMENT, GET_ASSIGNED_CONSENT_FORMS_LIST, GET_CLIENT_CONSENT_FORMS_LIST, GET_CONSENT_ASSIGNED_CHECKLISTS, GET_CONSENT_FORMS_LIST, GET_PARENT_CHILD_CONSENT_DETAILS, LIST_CONSENT_CHECKLISTS, MANAGE_PARENT_TASK_DETAIL, MANAGE_PARENT_TASK_SESSION, MANAGE_PARENTS_TASK_LIST, PREVIEW_ASSIGNED_CONSENT_FORM, REMOVE_ASSIGNED_GUARDIAN, RESEND_TEACHER_INVITE, UPDATE_CONSENT_CHECKLISTS, UPDATE_CONSENT_FORM_STATUS, VIEW_CONSENT_CHECKLISTS, VIEW_GUARDIAN_CLIENTS_DOCUMENTS } from "config/apiPaths"
import axiosBaseQuery from "utils/axios/axiosBaseQuery"

export const guardianApi = createApi({
    reducerPath: 'guardianApi',
    baseQuery: axiosBaseQuery({
        baseUrl: "/guardian",
    }),
    tagTypes: ["consentCheckLists", "consentCheckListDetails"],
    endpoints: (builder) => ({

        // remove assigned guardian
        removeAssignedGuardian: builder.mutation({
            query: ({ guardian, id }) => ({
                method: "DELETE",
                url: `/${guardian}/${REMOVE_ASSIGNED_GUARDIAN}/${id}`
            }),
        }),

        getConsentCheckLists: builder.query({
            query: ({ id, parentId, search = "", status = "", page = 1, limit = 10, sort = { column: "id", direction: "ASC" } }) => ({
                method: "PATCH",
                url: `${LIST_CONSENT_CHECKLISTS}/${parentId}/${id}`,
                data: { search, status, page, limit, sort }
            }),

            transformResponse: (response) => {
                return response.data;
            },
            providesTags: ["consentCheckLists"]
        }),

        createConsentCheckLists: builder.mutation({
            query: (data) => ({
                method: "POST",
                url: CREATE_CONSENT_CHECKLISTS,
                data
            }),
            invalidatesTags: ["consentCheckLists"]
        }),

        updateConsentCheckLists: builder.mutation({
            query: ({ id, data }) => ({
                method: "PUT",
                url: `${UPDATE_CONSENT_CHECKLISTS}/${id}`,
                data
            }),
            invalidatesTags: ["consentCheckLists", "consentCheckListDetails"]
        }),

        viewConsentCheckLists: builder.query({
            query: (id) => ({
                method: "GET",
                url: `${VIEW_CONSENT_CHECKLISTS}/${id}`

            }),
            transformResponse: (response) => {
                return response.data;
            },
            providesTags: ["consentCheckListDetails"]
        }),

        deleteConsentCheckLists: builder.mutation({
            query: ({ id }) => ({
                method: "DELETE",
                url: `${DELETE_CONSENT_CHECKLISTS}/${id}`
            }),
            invalidatesTags: ["consentCheckLists", "consentCheckListDetails"]
        }),

        getAssignedConsentFormsList: builder.query({
            query: ({ clientId, parentId, search = "", status = "", page = 1, limit = 10, sort = { column: "id", direction: "DESC" } }) => ({
                method: "PATCH",
                url: `${GET_ASSIGNED_CONSENT_FORMS_LIST}/${clientId}/${parentId}`,
                data: { search, status, page, limit, sort }
            }),

            transformResponse: (response) => {
                return response.data;
            },
            providesTags: ["assignedConsentFormsList"]
        }),

        getConsentFormsList: builder.query({
            query: ({ clientType }) => ({
                method: "GET",
                url: `${GET_CONSENT_FORMS_LIST}/${clientType}`,
            }),

            transformResponse: (response) => {
                return response.data;
            },
            providesTags: ["consentForms"]
        }),

        assignConsentForm: builder.mutation({
            query: ({ clientId, parentId, consent_form_id }) => ({
                method: "POST",
                url: `${ASSIGN_CONSENT_FORM}/${clientId}/${parentId}`,
                data: { consent_form_id }
            }),
            invalidatesTags: ["assignedConsentFormsList"]
        }),

        deleteAssignedConsentForm: builder.mutation({
            query: ({ id }) => ({
                method: "DELETE",
                url: `${DELETE_ASSIGNED_CONSENT_FORM}/${id}`
            }),
            invalidatesTags: ["assignedConsentFormsList"]
        }),

        assignConsentChecklists: builder.mutation({
            query: ({ body, id }) => ({
                method: "PUT",
                url: `${ASSIGN_CONSENT_CHECKLISTS}/${id}`,
                data: body
            }),
            invalidatesTags: ['consentCheckLists']
        }),

        previewAssignedConsentForm: builder.query({
            query: ({ id, mode = null }) => ({
                method: "GET",
                url: `${mode === "preview" ? PREVIEW_ASSIGNED_CONSENT_FORM : PREVIEW_ASSIGNED_CONSENT_FORM}/${id}`,
            }),
            transformResponse: (response) => {
                return response.data;
            },
        }),

        getGuardianClientDocuments: builder.query({
            query: ({ id, parentId, limit = 10, page = 1, search }) => ({
                method: "PATCH",
                url: `${VIEW_GUARDIAN_CLIENTS_DOCUMENTS}/${parentId}/${id}`,
                data: { limit, page, search }
            }),

            transformResponse: (response) => {
                return response.data;
            },

            providesTags: ['guardianDocumentList']
        }),

        deleteGuadianDocument: builder.mutation({
            query: ({ id }) => ({
                method: "DELETE",
                url: `${DELETE_GUARDIAN_DOCUMENT}/${id}`
            }),
            invalidatesTags: ["guardianDocumentList"]
        }),

        getConsentAssignedCheckLists: builder.query({
            query: ({ clientId, parentId }) => ({
                method: "PATCH",
                url: `${GET_CONSENT_ASSIGNED_CHECKLISTS}/${clientId}/${parentId}`,
            }),

            transformResponse: (response) => {
                return response.data;
            },

            providesTags: ['assignedConsentCheckLists']
        }),

        getGuardianAndChildConsentDetails: builder.query({
            query: ({ clientId, parentId }) => ({
                method: "GET",
                url: `${GET_PARENT_CHILD_CONSENT_DETAILS}/${parentId}/${clientId}`,
            }),

            transformResponse: (response) => {
                return response.data;
            },
        }),

        getParentTaskList: builder.query({
            query: ({ parentId, clientId, payload = { search: "", page: 1, limit: 10, sort: { column: "id", direction: "ASC" } } }) => ({
                method: "PATCH",
                url: `${MANAGE_PARENTS_TASK_LIST}/${parentId}/${clientId}`,
                data: { ...payload, task_for: "parent" }
            }),

            transformResponse: (response) => {
                return response.data;
            },
            providesTags: ['parentTaskList']
        }),

        getParentTaskDetailsStaff: builder.query({
            query: ({ parentId, clientId, taskId }) => {
                return ({
                    method: "GET",
                    url: `${MANAGE_PARENT_TASK_DETAIL}/${parentId}/${clientId}/${taskId}`
                })
            },
            transformResponse: (response) => {
                return response.data;
            },
        }),

        //Get Parent Task session data
        getParentTaskSessionView: builder.mutation({
            query: ({ parentId, clientId, taskId, payload = {} }) => ({
                method: "POST",
                url: `${MANAGE_PARENT_TASK_SESSION}/${parentId}/${clientId}/${taskId}`,
                data: payload,
            }),
        }),

        updateAssignedConsentFormStatus: builder.mutation({
            query: ({ body, id }) => ({
                method: "PATCH",
                url: `${UPDATE_CONSENT_FORM_STATUS}/${id}`,
                data: body
            }),
            invalidatesTags: ['assignedConsentFormsList']
        }),

         resendTeacherInvite: builder.mutation({
            query: ({ id }) => ({
                method: "POST",
                url: `${RESEND_TEACHER_INVITE}/${id}`,
                // data: body
            }),
            // invalidatesTags: ['assignedConsentFormsList']
        }),



    }),
})

export const {
    useCreateConsentCheckListsMutation,
    useUpdateConsentCheckListsMutation,
    useDeleteConsentCheckListsMutation,
    useViewConsentCheckListsQuery,
    useGetConsentCheckListsQuery,
    useGetAssignedConsentFormsListQuery,
    useLazyGetConsentFormsListQuery,
    useAssignConsentFormMutation,
    useDeleteAssignedConsentFormMutation,
    useAssignConsentChecklistsMutation,
    usePreviewAssignedConsentFormQuery,
    useGetGuardianClientDocumentsQuery,
    useGetConsentAssignedCheckListsQuery,
    useGetGuardianAndChildConsentDetailsQuery,
    useGetParentTaskListQuery,
    useLazyGetParentTaskDetailsStaffQuery,
    useGetParentTaskSessionViewMutation,
    useDeleteGuadianDocumentMutation,
    useUpdateAssignedConsentFormStatusMutation,
    useResendTeacherInviteMutation
} = guardianApi;